import React from 'react';
import {
  Package,
  DollarSign,
  Lightbulb,
  Zap,
  CheckCircle,
  Tag,
  FileText
} from 'lucide-react';
import MarkdownRenderer from '../../../components/common/MarkdownRenderer';
import EnhancedTableView from '../../../components/EnhancedTableView';
import { jsonrepair } from 'jsonrepair';

interface ContentData {
  hero_image?: { image: string; description?: string };
  headline?: string;
  consumer_insight?: string;
  main_benefit?: string;
  reasons_to_believe?: string[];
  price_point?: string | number;
  pack_size?: string | number;
  brand?: string;
  [key: string]: unknown;
}

interface TableData {
  type: string;
  title?: string;
  headers?: string[];
  data: any[];
}

interface ParsedResponse {
  output: string;
  analysis: string;
  tables: TableData[];
}

interface ConceptTestResultsProps {
  simulationResponse: string;
  contentData: ContentData;
  isPreparingExport?: boolean;
  onImageClick?: (src: string, alt: string) => void;
}

const ConceptTestResults: React.FC<ConceptTestResultsProps> = ({
  simulationResponse,
  contentData,
  isPreparingExport = false,
  onImageClick,
}) => {
  // Parse simulation response
  const parseResponse = (): ParsedResponse => {
    try {
      const parsed = JSON.parse(jsonrepair(simulationResponse));
      return {
        output: parsed.output || '',
        analysis: parsed.analysis || '',
        tables: Array.isArray(parsed.tables) ? parsed.tables : [],
      };
    } catch (error) {
      console.error('Error parsing concept test response:', error);
      return { output: '', analysis: '', tables: [] };
    }
  };

  const parsedResponse = parseResponse();

  // Extract product context from contentData
  const heroImage = contentData.hero_image;
  const headline = contentData.headline;
  const consumerInsight = contentData.consumer_insight;
  const mainBenefit = contentData.main_benefit;
  const reasonsToBelieve = contentData.reasons_to_believe || [];
  const pricePoint = contentData.price_point;
  const packSize = contentData.pack_size;
  const brand = contentData.brand;

  // Common styling
  const sectionCard = 'bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden';
  const sectionHeader = 'flex items-center p-4 bg-gradient-to-r from-gray-50 to-white border-b border-gray-100';
  const sectionTitle = 'text-base font-semibold text-gray-800';

  return (
    <div className="space-y-6">
      {/* Product Concept - Consolidated Section */}
      <div className={sectionCard}>
        <div className={sectionHeader}>
          <div className="p-2 bg-teal-100 rounded-lg mr-3">
            <Package className="h-5 w-5 text-teal-600" />
          </div>
          <h2 className={sectionTitle}>Product Concept</h2>
        </div>

        <div className="p-4">
          <div className="flex flex-col lg:flex-row gap-6">
            {/* Hero Image */}
            {heroImage?.image && (
              <div className="lg:w-1/3 flex-shrink-0">
                <div
                  className="relative bg-gray-50 rounded-lg border border-gray-200 p-3 cursor-pointer hover:border-teal-300 transition-colors"
                  onClick={() => onImageClick?.(heroImage.image, 'Product Hero Image')}
                >
                  <img
                    src={heroImage.image}
                    alt="Product Hero"
                    className="w-full h-48 object-contain rounded"
                  />
                </div>
                {heroImage.description && (
                  <p className="mt-2 text-xs text-gray-500 italic line-clamp-2">
                    {heroImage.description}
                  </p>
                )}
              </div>
            )}

            {/* Product Details - Compact Grid */}
            <div className="flex-1 space-y-3">
              {/* Top row: Brand, Price, Pack Size */}
              <div className="flex flex-wrap gap-2">
                {brand && (
                  <div className="flex items-center bg-indigo-50 rounded-lg px-3 py-1.5 border border-indigo-100">
                    <Tag className="h-3.5 w-3.5 text-indigo-600 mr-1.5" />
                    <span className="text-xs text-indigo-700 font-medium">{brand}</span>
                  </div>
                )}
                {pricePoint && (
                  <div className="flex items-center bg-green-50 rounded-lg px-3 py-1.5 border border-green-100">
                    <DollarSign className="h-3.5 w-3.5 text-green-600 mr-1" />
                    <span className="text-xs text-green-700 font-medium">{pricePoint}</span>
                  </div>
                )}
                {packSize && (
                  <div className="flex items-center bg-blue-50 rounded-lg px-3 py-1.5 border border-blue-100">
                    <Package className="h-3.5 w-3.5 text-blue-600 mr-1.5" />
                    <span className="text-xs text-blue-700 font-medium">{packSize}</span>
                  </div>
                )}
              </div>

              {/* Headline */}
              {headline && (
                <div>
                  <div className="flex items-center mb-1">
                    <FileText className="h-3.5 w-3.5 text-gray-500 mr-1" />
                    <span className="text-xs font-semibold text-gray-500 uppercase">Headline</span>
                  </div>
                  <p className="text-sm font-medium text-gray-900">{headline}</p>
                </div>
              )}

              {/* Consumer Insight */}
              {consumerInsight && (
                <div>
                  <div className="flex items-center mb-1">
                    <Lightbulb className="h-3.5 w-3.5 text-amber-500 mr-1" />
                    <span className="text-xs font-semibold text-gray-500 uppercase">Consumer Insight</span>
                  </div>
                  <p className="text-sm text-gray-700">{consumerInsight}</p>
                </div>
              )}

              {/* Main Benefit */}
              {mainBenefit && (
                <div>
                  <div className="flex items-center mb-1">
                    <Zap className="h-3.5 w-3.5 text-purple-500 mr-1" />
                    <span className="text-xs font-semibold text-gray-500 uppercase">Main Benefit</span>
                  </div>
                  <p className="text-sm text-gray-700">{mainBenefit}</p>
                </div>
              )}

              {/* Reasons to Believe */}
              {reasonsToBelieve.length > 0 && (
                <div>
                  <div className="flex items-center mb-1">
                    <CheckCircle className="h-3.5 w-3.5 text-green-500 mr-1" />
                    <span className="text-xs font-semibold text-gray-500 uppercase">Reasons to Believe</span>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {reasonsToBelieve.map((rtb, index) => (
                      <span
                        key={index}
                        className="text-xs bg-green-50 text-green-700 px-2 py-1 rounded border border-green-100"
                      >
                        {rtb}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Summary Card (Markdown Output) */}
      {parsedResponse.output && (
        <div className={sectionCard}>
          <div className="p-4">
            <MarkdownRenderer
              content={parsedResponse.output}
              variant="default"
              bordered={false}
              isExporting={isPreparingExport}
            />
          </div>
        </div>
      )}

      {/* Verdicts Table */}
      {parsedResponse.tables.map((table, index) => (
        table.type === 'table' && table.headers && table.data && (
          <div key={index} className={sectionCard}>
            <EnhancedTableView
              headers={table.headers}
              data={table.data}
              title={table.title}
              pdfMode={false}
              showExportButtons={true}
              showFullContent={true}
              images={[]}
              isExporting={isPreparingExport}
            />
          </div>
        )
      ))}
    </div>
  );
};

export default ConceptTestResults;
